import React, { useState, useEffect } from 'react'
import debounce from 'lodash/debounce'
import PropTypes from 'prop-types'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import PlaylistActions, { thunks as PlayListThunks } from 'reducers/PlayList'
import ModalActions from 'reducers/Modal'
import NotificationActions from 'reducers/Notification'
import { NotificationType } from 'constants/enums'
import { ModalWrapper } from 'components'
import { connect } from 'react-redux'
import { useDispatch } from 'react-redux'
import { Button, TextField, MaterialIcon } from '@dsny/dsny-component-library'
import {
  Title,
  Content,
  XButtonWrapper,
  ButtonWrapper,
  DescriptionCounter
} from 'components/Modals/CreatePlaylistModal/CreatePlaylistModal.styles'

const MAX_TITLE_LIMIT = 50
const MAX_DESCRIPTION_LIMIT = 100

const mapStateToProps = ({ playList }) => ({
  playlist: playList.playlistUnderEditing,
  songsToAdd: playList.songsToAdd
})

function PlaylistModal({ playlist, songsToAdd = [] }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const [title, setTitle] = useState(playlist?.name || '')
  const [message, setMessage] = useState(playlist?.desc || '')
  const [hasTitleError, setHasTitleError] = useState(false)

  const checkTitle = debounce(() => {
    if (title) {
      setHasTitleError(false)
    }
  }, 300)

  useEffect(() => {
    return () => {
      dispatch(PlaylistActions.setPlaylistUnderEditing())
    }
  }, [])

  useEffect(() => {
    checkTitle()
  }, [title])

  const handleCreateButtonClick = () => {
    if (!title) {
      setHasTitleError(true)

      return
    }
    if (!playlist) {
      dispatch(
        PlayListThunks.create(
          { name: title, desc: message, default: false },
          ({ playlistid }) => {
            if (Array.isArray(songsToAdd) && songsToAdd.length > 0) {
              dispatch(PlayListThunks.addTracks(playlistid, songsToAdd))
              dispatch(PlaylistActions.setSongsToAdd([]))
            }
            dispatch(
              NotificationActions.broadcastMessage(
                title,
                NotificationType.playlistCreated
              )
            )
            handleCancelButtonClick()
          }
        )
      )
    } else {
      const newPlaylist = { ...playlist, name: title, desc: message }
      dispatch(
        PlayListThunks.updatePlaylist(newPlaylist, () => {
          dispatch(
            NotificationActions.broadcastMessage(
              title,
              NotificationType.playlistUpdated
            )
          )
          handleCancelButtonClick()
        })
      )
    }
  }

  const handleCancelButtonClick = () => {
    dispatch(ModalActions.togglePlaylistModal())
  }

  const updateText = (value, field, limit) => {
    if (value.length <= limit) {
      const setters = {
        title: setTitle,
        message: setMessage
      }

      if (setters[field]) {
        setters[field](value)
      }
    }
  }

  return (
    <div className="fixed top-0 left-0 flex w-full h-full justify-around items-center bg-modalBackdrop z-50">
      <ModalWrapper
        className="rounded shadow relative"
        style={{
          width: '95vw',
          padding: '30px 25px 64px 64px',
          maxWidth: 548,
          background: theme.colors.neutralW20
        }}
        onClickOutside={handleCancelButtonClick}
      >
        <XButtonWrapper onClick={handleCancelButtonClick}>
          <MaterialIcon name="X" color={theme.colors.neutralW80} />
        </XButtonWrapper>
        <Content>
          <Title>{t('playlist.create-a-new-playlist')}</Title>
          <TextField
            autoFocus
            wrapperStyles={{ marginBottom: 24, paddingRight: 0 }}
            value={title}
            required
            error={hasTitleError ? t('playlist.playlist-name-input-error') : ''}
            errorStyle={{ marginTop: 8, color: theme.colors.error70 }}
            labelText={t('playlist.playlist-name')}
            setting="player"
            onChange={(event) => {
              updateText(event.currentTarget.value, 'title', MAX_TITLE_LIMIT)
            }}
          />

          <TextField
            multiline
            wrapperStyles={{ paddingRight: 0, marginBottom: 4 }}
            maxLength={MAX_DESCRIPTION_LIMIT}
            value={message}
            onChange={(event) => {
              updateText(
                event.currentTarget.value,
                'message',
                MAX_DESCRIPTION_LIMIT
              )
            }}
            labelText={t('playlist.description-optional-label')}
            setting="player"
          />

          <DescriptionCounter>{`${message?.length}/${MAX_DESCRIPTION_LIMIT}`}</DescriptionCounter>

          <ButtonWrapper>
            <Button
              variant="primary"
              setting="player"
              mode="dark"
              onClick={handleCreateButtonClick}
            >
              {playlist
                ? t('playlist.update-playlist')
                : t('playlist.create-playlist')}
            </Button>
          </ButtonWrapper>
        </Content>
      </ModalWrapper>
    </div>
  )
}

PlaylistModal.propTypes = {
  playlist: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  songsToAdd: PropTypes.arrayOf(PropTypes.number)
}

export default connect(mapStateToProps)(PlaylistModal)
